import {
  AutomationExecutionStatus,
  AutomationStatus,
  BackgroundStyle,
  CommunicationPublishedStatus,
} from "../../../config/enums";
import { formatDateValue } from "../../../utils/date";

// An execution as the legacy services and the mapped rows have it: PascalCase when it has
// just been read, camelCase once mapped, and both are read here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AutomationExecution = Record<string, any>;

export const getAutomationExecutionStatusName = (execution: AutomationExecution) =>
  execution?.StatusName || "";

export const isFixtureExecution = (execution: AutomationExecution) =>
  execution?.relatedEntityType === "fixture";

const terminalStatuses: number[] = [
  AutomationExecutionStatus.Cancelled,
  AutomationExecutionStatus.Completed,
  AutomationExecutionStatus.Expired,
  AutomationExecutionStatus.Failed,
];

const terminalStatusNames = ["Cancelled", "Completed", "Expired", "Failed"];

export const isTerminalAutomationExecution = (execution: AutomationExecution) =>
  terminalStatuses.some((status) => String(execution?.status) === String(status)) ||
  terminalStatusNames.includes(execution?.statusName);

/** A fixture execution's own copy of the communication has to be published before it sends. */
export const needsCommunicationPublishing = (execution: AutomationExecution) =>
  isFixtureExecution(execution) &&
  !isTerminalAutomationExecution(execution) &&
  Boolean(execution?.communicationId) &&
  String(execution?.executionCommunicationStatusCode) !==
    String(CommunicationPublishedStatus.Published);

/** Whether the communication for a fixture's execution is still to be copied for it. */
export const canPrepareAutomationExecutionCommunication = (
  execution: AutomationExecution
) =>
  isFixtureExecution(execution) &&
  !execution?.communicationId &&
  execution?.automationId &&
  execution?.fixtureId &&
  !isTerminalAutomationExecution(execution);

/** An execution that is pending, with its communication published, can be sent early. */
export function canSendAutomationExecutionNow(execution: AutomationExecution) {
  const executionId = execution?.id || execution?.executionId;
  const isPendingExecution =
    String(execution?.status) === String(AutomationExecutionStatus.Pending) ||
    String(execution?.statusName).toLowerCase() === "pending";
  const isPublishedCommunication =
    String(execution?.executionCommunicationStatusCode) ===
    String(CommunicationPublishedStatus.Published);

  return Boolean(
    executionId &&
      isPendingExecution &&
      execution?.communicationId &&
      isPublishedCommunication &&
      !execution?.needsCommunicationPublishing &&
      !execution?.updateCommunicationLink &&
      !execution?.canPrepare
  );
}

/** Whether the automation was running as a test or live when the execution was made. */
export function getAutomationExecutionModeStatus(automationStatus: unknown) {
  if (String(automationStatus) === String(AutomationStatus.Testing)) {
    return "Test";
  }

  if (String(automationStatus) === String(AutomationStatus.Published)) {
    return "Live";
  }

  return "";
}

export function getAutomationExecutionModeStatusLabel(automationStatus: unknown) {
  const modeStatus = getAutomationExecutionModeStatus(automationStatus);

  return modeStatus ? `${modeStatus} mode` : "";
}

export function getAutomationExecutionModeStatusBackground(automationStatus: unknown) {
  if (String(automationStatus) === String(AutomationStatus.Testing)) {
    return BackgroundStyle.Warning;
  }

  if (String(automationStatus) === String(AutomationStatus.Published)) {
    return BackgroundStyle.Success;
  }

  return "";
}

/** When the communication for a fixture's execution will be copied, if it is yet to be. */
export function getContentPreparationText(contentPreparationStartsAt: unknown) {
  const contentPreparationText = formatDateValue(
    contentPreparationStartsAt,
    "DD/MM/YYYY HH:mm"
  );

  return contentPreparationText ? `Content copy starts ${contentPreparationText}` : "";
}
